import { NFT, ThirdwebSDK } from '@thirdweb-dev/sdk';
import { BigNumber, ethers } from 'ethers';

import { ListingType } from 'models/lootbox/i-lootbox-purchase-config.interface';
import { ILootboxListingResult } from 'models/lootbox/i-lootbox-trade.interface';
import { ILootboxListingTransactionConfiguration } from 'models/web3Singleton/i-lootbox-listing-transaction-configuration.interface';
import { ILootboxPackTransactionConfiguration } from 'models/web3Singleton/i-lootbox-pack-transaction-configuration.interface';

import { PriceCurrencySymbols } from 'enums/price-currency-symbols.enum';

import { LOOT_BOX_STORE_ABI } from 'constants/abi/loot-box-store-abi.constant';
import { GAS_LIMITS } from 'constants/gas-limits.constant';
import { NETWORK_SETTINGS } from 'constants/network.constant';

import { BaseWeb3Service } from './base-web3.service';

/**
 * Service for handling lootbox contract interactions
 * Manages lootbox listings, purchases, and opening operations
 */
export class LootboxService extends BaseWeb3Service {
  private readonly lootboxStoreAbi = LOOT_BOX_STORE_ABI;
  private readonly lootboxStoreAddress = NETWORK_SETTINGS.LOOTBOX_STORE.ADDRESS;
  private readonly lootboxMarketplaceAddress = NETWORK_SETTINGS.LOOTBOX_MARKETPLACE.ADDRESS;
  private readonly lootboxPackAddress = NETWORK_SETTINGS.LOOTBOX_PACK.ADDRESS;

  /**
   * Gets the chain name for Thirdweb SDK
   */
  private getChainName(): string {
    const isDevelopment = process.env.REACT_APP_DEVELOPMENT_MODE === 'true';
    return isDevelopment ? 'amoy' : 'polygon';
  }

  /**
   * Gets lootboxes owned by a wallet address
   */
  async getLootboxesOwned(walletAddress: string, packContractAddress: string): Promise<NFT[]> {
    const ethersProvider = new ethers.providers.Web3Provider(this.web3.currentProvider as any);
    const sdk = ThirdwebSDK.fromSigner(ethersProvider.getSigner(), this.getChainName());
    const packContract = await sdk.getContract(packContractAddress, 'pack');

    return packContract.erc1155.getOwned(walletAddress);
  }

  /**
   * Gets all valid marketplace lootbox listings
   */
  async getMarketplaceLootboxesListings(): Promise<ILootboxListingResult[]> {
    const ethersProvider = new ethers.providers.Web3Provider(this.web3.currentProvider as any);
    const sdk = ThirdwebSDK.fromSigner(ethersProvider.getSigner(), this.getChainName());
    const marketplaceContract = await sdk.getContract(this.lootboxMarketplaceAddress, 'marketplace-v3');

    const listingsData = await marketplaceContract.directListings.getAllValid();

    return listingsData.map((listing) => {
      const value = listing.pricePerToken;
      const displayValue = BigNumber.from(value)
        .div(BigNumber.from(10).pow(listing.currencyValuePerToken.decimals))
        .toString();

      return {
        id: listing.id,
        asset: { id: listing.asset.id },
        quantity: +listing.quantity,
        currencyValuePerToken: {
          symbol:
            listing.currencyValuePerToken.symbol === PriceCurrencySymbols.RMV
              ? PriceCurrencySymbols.RMV
              : PriceCurrencySymbols.USDC,
          value,
          displayValue,
        },
        type: ListingType.MarketplaceV3,
      };
    });
  }

  /**
   * Purchases a marketplace lootbox listing
   */
  async purchaseMarketplaceLootboxListing(configuration: ILootboxListingTransactionConfiguration): Promise<void> {
    const ethersProvider = new ethers.providers.Web3Provider(this.web3.currentProvider as any);
    const sdk = ThirdwebSDK.fromSigner(ethersProvider.getSigner(), this.getChainName());
    const marketplaceContract = await sdk.getContract(this.lootboxMarketplaceAddress, 'marketplace-v3');

    return marketplaceContract.directListings
      .buyFromListing(configuration.listingId, configuration.quantity, configuration.walletAddress)
      .then(({ receipt }) => {
        if (receipt.transactionHash) {
          configuration.onTransactionHash(receipt.transactionHash);
        }
      });
  }

  /**
   * Gets all valid store lootbox listings
   */
  async getLootboxListings(): Promise<ILootboxListingResult[]> {
    const contractAddress = this.lootboxStoreAddress;
    const contract = this.createContract(this.lootboxStoreAbi, contractAddress);

    await this.updateNetwork();

    const listingsData = await contract.methods.getAllValidListings().call();

    return listingsData[0].map((listingId: number, index: number) => {
      const assetId = listingsData[2][index];
      const quantity = listingsData[3][index];
      const value = listingsData[4][index];
      const displayValue = BigNumber.from(value).div(BigNumber.from(10).pow(18)).toString();

      return {
        id: listingId,
        asset: { id: assetId },
        quantity: +quantity,
        currencyValuePerToken: {
          symbol: PriceCurrencySymbols.RMV,
          value,
          displayValue,
        },
        type: ListingType.Contract,
      };
    });
  }

  /**
   * Purchases a store lootbox listing
   */
  async purchaseLootboxListing(configuration: ILootboxListingTransactionConfiguration): Promise<void> {
    const contractAddress = this.lootboxStoreAddress;
    const contract = this.createContract(this.lootboxStoreAbi, contractAddress);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    await contract.methods
      .safePurchase(configuration.listingId, configuration.quantity, configuration.forMaxPriceInQT)
      .send({ from: configuration.walletAddress, gasPrice })
      .on('transactionHash', (hash: string) => configuration.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && configuration.offerAccepted())
      .on('error', (error: Error) => {
        console.error('Purchase lootbox listing transaction failed:', error);
        configuration.offerCanceled();
      });
  }

  /**
   * Checks the price of a lootbox listing
   */
  async checkLootboxPrice(listingId: string): Promise<string> {
    const contractAddress = this.lootboxStoreAddress;
    const contract = this.createContract(this.lootboxStoreAbi, contractAddress);

    await this.updateNetwork();

    return contract.methods.checkListingQTPrice(listingId).call();
  }

  async openLootbox(configuration: ILootboxPackTransactionConfiguration): Promise<any> {
    const ethersProvider = new ethers.providers.Web3Provider(this.web3.currentProvider as any);
    const sdk = ThirdwebSDK.fromSigner(ethersProvider.getSigner(), this.getChainName());
    const packContractAddress = configuration.address || this.lootboxPackAddress;
    const packContract = await sdk.getContract(packContractAddress, 'pack');
    const transaction = await packContract.open.prepare(configuration.id, configuration.quantity);

    const gasPriceBase = await this.getGasPrice();
    transaction.setGasPrice(gasPriceBase);

    // Calculate gas limit based on quantity, with a max batch limit
    const effectiveQuantity = Math.min(configuration.quantity, GAS_LIMITS.MAX_LOOTBOX_OPEN_BATCH);
    const gasLimit = BigNumber.from(
      GAS_LIMITS.LOOTBOX_OPEN_BASE + GAS_LIMITS.LOOTBOX_OPEN_PER_ITEM * effectiveQuantity
    );
    transaction.setGasLimit(gasLimit);

    return await transaction.execute();
  }
}
